/**
 * Onboarding illustrations - Apple-inspired, universal, no people.
 *
 * Pure SVG so they render instantly (no image decode), scale crisply on every
 * device, theme automatically via `currentColor` + design tokens, and respect
 * `prefers-reduced-motion`. Bold geometry, generous negative space - the
 * Human Interface Guidelines look (Health, Wallet, FaceTime app icons).
 */
import { motion, useReducedMotion } from "framer-motion";

type Props = { className?: string };

const ringEase = [0.16, 1, 0.3, 1] as const;

/** Slide 1 - Call home: concentric signal rings around a globe pin. */
export function IllustrationCall({ className }: Props) {
  const reduce = useReducedMotion();
  return (
    <svg
      viewBox="0 0 280 280"
      className={className}
      role="img"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="ob-call-bg" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="hsl(var(--primary) / 0.18)" />
          <stop offset="100%" stopColor="hsl(var(--primary) / 0.02)" />
        </linearGradient>
        <linearGradient id="ob-call-pin" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="hsl(var(--primary))" />
          <stop offset="100%" stopColor="hsl(var(--primary) / 0.7)" />
        </linearGradient>
      </defs>

      <rect width="280" height="280" rx="48" fill="url(#ob-call-bg)" />

      {/* Signal rings */}
      {[0, 1, 2].map((i) => (
        <motion.circle
          key={i}
          cx="140"
          cy="150"
          r={40 + i * 30}
          fill="none"
          stroke="hsl(var(--primary))"
          strokeOpacity={0.35 - i * 0.08}
          strokeWidth={2}
          initial={reduce ? false : { scale: 0.7, opacity: 0 }}
          animate={
            reduce
              ? { opacity: 0.35 - i * 0.08 }
              : { scale: [0.7, 1.05, 1], opacity: [0, 0.45 - i * 0.1, 0.35 - i * 0.08] }
          }
          transition={{
            duration: 1.6,
            delay: 0.15 * i,
            ease: ringEase,
            repeat: reduce ? 0 : Infinity,
            repeatDelay: 1.4,
          }}
          style={{ transformOrigin: "140px 150px" }}
        />
      ))}

      {/* Pin */}
      <motion.g
        initial={reduce ? false : { y: -6, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: ringEase }}
      >
        <path
          d="M140 100 C 118 100 102 117 102 138 C 102 165 140 198 140 198 C 140 198 178 165 178 138 C 178 117 162 100 140 100 Z"
          fill="url(#ob-call-pin)"
        />
        <circle cx="140" cy="136" r="13" fill="hsl(var(--background))" />
      </motion.g>
    </svg>
  );
}

/** Slide 2 - Private by design: shield with a soft check. */
export function IllustrationPrivacy({ className }: Props) {
  const reduce = useReducedMotion();
  return (
    <svg
      viewBox="0 0 280 280"
      className={className}
      role="img"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="ob-shield-bg" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="hsl(var(--primary) / 0.18)" />
          <stop offset="100%" stopColor="hsl(var(--primary) / 0.02)" />
        </linearGradient>
        <linearGradient id="ob-shield" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="hsl(var(--primary))" />
          <stop offset="100%" stopColor="hsl(var(--primary) / 0.75)" />
        </linearGradient>
      </defs>

      <rect width="280" height="280" rx="48" fill="url(#ob-shield-bg)" />

      <motion.g
        initial={reduce ? false : { scale: 0.85, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.6, ease: ringEase }}
        style={{ transformOrigin: "140px 140px" }}
      >
        <path
          d="M140 70 L 200 92 L 200 150 C 200 184 175 208 140 218 C 105 208 80 184 80 150 L 80 92 Z"
          fill="url(#ob-shield)"
        />
        <motion.path
          d="M112 144 L 134 166 L 172 122"
          fill="none"
          stroke="hsl(var(--background))"
          strokeWidth="10"
          strokeLinecap="round"
          strokeLinejoin="round"
          initial={reduce ? false : { pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 0.7, delay: 0.35, ease: "easeOut" }}
        />
      </motion.g>
    </svg>
  );
}

/** Slide 3 - Top up & talk: wallet card with rising coin. */
export function IllustrationWallet({ className }: Props) {
  const reduce = useReducedMotion();
  return (
    <svg
      viewBox="0 0 280 280"
      className={className}
      role="img"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="ob-wallet-bg" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="hsl(var(--primary) / 0.18)" />
          <stop offset="100%" stopColor="hsl(var(--primary) / 0.02)" />
        </linearGradient>
        <linearGradient id="ob-card" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="hsl(var(--primary))" />
          <stop offset="100%" stopColor="hsl(var(--primary) / 0.7)" />
        </linearGradient>
        <linearGradient id="ob-coin" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="hsl(var(--primary) / 0.95)" />
          <stop offset="100%" stopColor="hsl(var(--primary) / 0.6)" />
        </linearGradient>
      </defs>

      <rect width="280" height="280" rx="48" fill="url(#ob-wallet-bg)" />

      {/* Card */}
      <motion.g
        initial={reduce ? false : { y: 10, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.55, ease: ringEase }}
      >
        <rect x="60" y="130" width="160" height="100" rx="20" fill="url(#ob-card)" />
        <rect x="76" y="150" width="46" height="10" rx="5" fill="hsl(var(--background) / 0.55)" />
        <rect x="76" y="170" width="80" height="8" rx="4" fill="hsl(var(--background) / 0.35)" />
        <circle cx="194" cy="200" r="12" fill="hsl(var(--background) / 0.5)" />
        <circle cx="180" cy="200" r="12" fill="hsl(var(--background) / 0.3)" />
      </motion.g>

      {/* Rising coin */}
      <motion.g
        initial={reduce ? false : { y: 30, opacity: 0 }}
        animate={reduce ? { opacity: 1 } : { y: [30, -4, 0], opacity: 1 }}
        transition={{ duration: 1, delay: 0.3, ease: ringEase }}
      >
        <circle cx="140" cy="92" r="34" fill="url(#ob-coin)" />
        <text
          x="140"
          y="104"
          textAnchor="middle"
          fontSize="34"
          fontWeight="700"
          fill="hsl(var(--background))"
          fontFamily="system-ui, -apple-system, sans-serif"
        >
          $
        </text>
      </motion.g>
    </svg>
  );
}
